"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, ShieldCheck, Sparkles, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
const features = ["Multi-tier TimberTech and Trex composite decking with hidden fasteners", "Architectural Western Red Cedar beamed covered porches and pavilions", "Masonry stacked-stone fireplaces and integrated gas fire features", "High-airflow phantom screens and ventilation for Atlanta summer humidity", "Fully plumbed outdoor kitchens with stainless cabinetry and stone counters"];
const materials = [{
  label: "Decking Core",
  value: "TimberTech Advanced PVC / Composite"
}, {
  label: "Timber Framing",
  value: "#1 Grade Western Red Cedar"
}, {
  label: "Masonry",
  value: "Locally Sourced Stacked Fieldstone"
}, {
  label: "Permitting",
  value: "Full City of Brookhaven Building Clearance"
}];
export default function OutdoorLiving() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="outdoor-living-decks" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden text-foreground">
      <div className="container mx-auto px-5 sm:px-8 lg:px-16 max-w-7xl">
        <motion.div initial={prefersReducedMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 15
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: Narrative (5 columns) */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="mb-4">
              <Badge variant="outline" className="bg-primary/10 border-primary/30 text-primary-foreground font-semibold px-3 py-1 rounded-full text-xs uppercase tracking-wider inline-flex items-center gap-1.5">
                <Sparkles className="size-3.5 text-primary" />
                Brookhaven Outdoor Living
              </Badge>
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground tracking-tight leading-[1.18] mb-6">
              Expand your living room under Georgia skies.
            </h2>

            <p className="text-base sm:text-lg text-foreground/80 leading-relaxed mb-6 font-sans">
              Step outside to covered cedar porches, heat-ready stone fireplaces, and low-maintenance composite decks built for year-round Brookhaven entertaining. We design weatherized structures that balance southern heat, seasonal rainfall, and natural neighborhood canopy.
            </p>

            {/* Architectural Feature Checklist */}
            <div className="space-y-3.5 mb-8">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <div className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/20 text-primary-foreground border border-primary/40">
                    <Check className="size-3 text-primary stroke-[2.5]" />
                  </div>
                  <span className="text-sm sm:text-base text-foreground font-sans leading-snug">
                    {feature}
                  </span>
                </div>)}
            </div>

            {/* CTA action cluster */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-7 py-3.5 shadow-sm font-semibold transition-[color,background-color,border-color,box-shadow] duration-200">
                <Link to="/ContactRequestAnEstimatePage">
                  Consult on outdoor design
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted/70 rounded-lg px-6 py-3.5 font-medium transition-[color,background-color,border-color,box-shadow] duration-200">
                <Link to="/OurProjectsPage">
                  <Compass className="size-4 mr-1 text-primary" />
                  View completed spaces
                </Link>
              </Button>
            </div>

            {/* Local permit assurance callout */}
            <div className="mt-8 pt-6 border-t border-border flex items-center gap-3 text-xs sm:text-sm text-foreground/75 font-sans">
              <ShieldCheck className="size-5 text-primary shrink-0" />
              <span>
                Engineered for Fulton and DeKalb County zoning, lot coverage thresholds, and HOA standards.
              </span>
            </div>
          </div>

          {/* Right Column: Architectural Visuals & Floating Highlight Plinth (7 columns) */}
          <div className="lg:col-span-7 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border/80 aspect-[4/3] sm:aspect-[16/11]">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_covered_porch_composite_deck.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_covered_porch_composite_deck.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/luxury_covered_porch_composite_deck.png-wvc-sizes" alt="Custom covered cedar porch and multi-level composite deck with outdoor kitchen and stone fireplace in Brookhaven" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/80 via-secondary/15 to-transparent pointer-events-none" />

              <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-border text-xs font-semibold text-foreground tracking-wide uppercase">
                Brookhaven, GA Residence
              </div>
            </div>

            {/* Atelier Glass Highlight Card */}
            <motion.div animate={prefersReducedMotion ? {
            y: 0
          } : {
            y: [0, -6, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }} className="lg:absolute lg:-bottom-8 lg:left-6 lg:right-6 mt-6 lg:mt-0">
              <Card className="rounded-2xl border border-border/90 bg-card/95 backdrop-blur-md shadow-2xl p-5 sm:p-6 transition-[border-color,box-shadow] duration-250 hover:border-primary/50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-3.5 mb-3.5 border-b border-border gap-2">
                  <div>
                    <span className="text-xs uppercase font-semibold tracking-wider text-primary">
                      Material Specification
                    </span>
                    <h3 className="font-serif text-lg sm:text-xl font-semibold text-card-foreground">
                      Bespoke Exterior Assembly
                    </h3>
                  </div>
                  <div className="text-xs text-foreground/70 sm:text-right font-mono">
                    Built for Southern Climates
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs sm:text-sm">
                  {materials.map((item, idx) => <div key={idx} data-index={idx} className="flex flex-col">
                      <span className="text-foreground/60 text-xs font-medium uppercase tracking-wider">
                        {item.label}
                      </span>
                      <span className="text-card-foreground font-semibold font-sans mt-0.5">
                        {item.value}
                      </span>
                    </div>)}
                </div>
              </Card>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}